const MAX_CACHE = 50;

class BlogSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.blogContent = document.querySelector("[data-blog-content]");
    if (!this.input || !this.blogContent) return;
    this.resultsContainer = this.querySelector("[data-blog-search-results]");
    this.clearBtn = this.querySelector("[data-blog-search-clear]");
    this.spinner = this.querySelector("[data-blog-search-spinner]");
    this.cachedResults = {};
    this.abortController = null;
    this.blogHandle = this.dataset.blogHandle || "";
    this.articleIndex = this.loadArticleIndex();

    this._onInput = this.debounce(() => this.onInput(), 300);
    this._onClear = () => this.clearSearch();
    this._onSubmit = (e) => e.preventDefault();

    this.input.addEventListener("input", this._onInput);
    this.clearBtn?.addEventListener("click", this._onClear);
    this.input.form?.addEventListener("submit", this._onSubmit);
  }

  disconnectedCallback() {
    this.input?.removeEventListener("input", this._onInput);
    this.clearBtn?.removeEventListener("click", this._onClear);
    this.input?.form?.removeEventListener("submit", this._onSubmit);
    this.abortController?.abort();
  }

  loadArticleIndex() {
    try {
      const el = document.getElementById("blog-article-data");
      if (!el) return [];
      const articles = JSON.parse(el.textContent);
      for (const article of articles) {
        article.normalizedUrl = article.url.replace(/\/+$/, "").toLowerCase();
        article.searchTags = (article.tags || []).join(",").toLowerCase();
        article.searchTitle = (article.title || "").toLowerCase();
      }
      return articles;
    } catch {
      return [];
    }
  }

  buildCardHtml(article) {
    const tagsHtml = article.tags?.length
      ? `<ul class="article-card__tags">
          ${article.tags
            .map(
              (tag) =>
                `<li class="article-card__tag body-small color-background-1">
              <a href="/blogs/${this.blogHandle}/tagged/${tag.toLowerCase().replace(/\s+/g, "-")}">
                <span title="${this.escapeHtml(tag)}">${this.escapeHtml(tag)}</span>
              </a>
            </li>`,
            )
            .join("")}
        </ul>`
      : "";

    const imageHtml = article.image
      ? `<div class="article-card__image media">
          <img src="${this.escapeHtml(article.image.src)}" alt="${this.escapeHtml(article.image.alt)}" loading="lazy" width="720">
        </div>`
      : "";

    const excerptHtml = article.excerpt
      ? `<p class="article-card__excerpt">${this.escapeHtml(article.excerpt)}</p>`
      : "";

    return `<article class="article-card article-card--landscape">
      ${imageHtml}
      ${tagsHtml}
      <div class="article-content motion-reduce">
        <div class="article-card__info">
          <header class="article-card__header">
            <h2 class="article-card__title h5">
              <a href="${this.escapeHtml(article.url)}">${this.escapeHtml(article.title)}</a>
            </h2>
            ${excerptHtml}
          </header>
          <div class="article-card__bottom">
            <div class="article-card__author">
              <div class="article-card__author-info">
                ${article.author ? `<span class="article-card__author-name">${this.escapeHtml(article.author)}</span>` : ""}
                ${article.published_at ? `<div class="article-card__meta"><span class="article-card__date body-small">${this.escapeHtml(article.published_at)}</span></div>` : ""}
              </div>
            </div>
          </div>
        </div>
      </div>
      <a class="article-card-link" href="${this.escapeHtml(article.url)}"></a>
    </article>`;
  }

  escapeHtml(str) {
    const div = document.createElement("div");
    div.textContent = str || "";
    return div.innerHTML;
  }

  debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }

  onInput() {
    const term = this.input.value.trim();
    this.clearBtn.classList.toggle("hidden", term.length === 0);

    if (!term.length) {
      this.clearSearch();
      return;
    }

    this.fetchResults(term);
  }

  getIndexMatches(term) {
    const query = term.toLowerCase();
    const matches = [];
    const onPageUrls = new Set();

    // First, collect matches from on-page DOM elements (they have full rendered HTML)
    for (const li of this.blogContent.querySelectorAll(
      ".blog__post.article[data-tags]",
    )) {
      const tags = li.dataset.tags ?? "";
      const title = li.dataset.title ?? "";
      if (tags.includes(query) || title.includes(query)) {
        const url = this.extractArticleUrl(li);
        onPageUrls.add(url);
        matches.push({ html: li.innerHTML, source: "local" });
      }
    }

    // Then, search the full article index for off-page matches
    for (const article of this.articleIndex) {
      if (onPageUrls.has(article.normalizedUrl)) continue;
      if (
        article.searchTags.includes(query) ||
        article.searchTitle.includes(query)
      ) {
        matches.push({
          html: this.buildCardHtml(article),
          source: "index",
          url: article.normalizedUrl,
        });
      }
    }

    return { matches, onPageUrls };
  }

  normalizeUrl(url) {
    if (!url) return "";
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname.replace(/\/+$/, "").toLowerCase();
    } catch {
      return url.split("?")[0].replace(/\/+$/, "").toLowerCase();
    }
  }

  extractArticleUrl(el) {
    const overlayLink = el.querySelector("a.article-card-link");
    if (overlayLink)
      return this.normalizeUrl(overlayLink.getAttribute("href"));

    for (const link of el.querySelectorAll("a[href]")) {
      const href = link.getAttribute("href") ?? "";
      if (href.includes("/blogs/")) return this.normalizeUrl(href);
    }

    return "";
  }

  mergeResults(indexMatches, onPageUrls, apiEls) {
    const seen = new Set(onPageUrls);
    const results = [];

    for (const match of indexMatches) {
      const url = match.url || "";
      if (url) seen.add(url);
      results.push(match);
    }

    for (const li of apiEls) {
      const url = this.extractArticleUrl(li);
      if (url && !seen.has(url)) {
        seen.add(url);
        results.push({ html: li.innerHTML, source: "api" });
      }
    }

    return results;
  }

  fetchResults(term) {
    const cacheKey = term.toLowerCase().replace(/\s+/g, "-");

    this.abortController?.abort();
    this.abortController = new AbortController();

    if (this.cachedResults[cacheKey]) {
      this.renderResults(this.cachedResults[cacheKey]);
      return;
    }

    this.spinner.classList.remove("hidden");
    this.blogContent.classList.add("hidden");
    this.resultsContainer.classList.add("hidden");

    const { matches: indexMatches, onPageUrls } = this.getIndexMatches(term);

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(term)}&resources[type]=article&resources[limit]=20&section_id=predictive-search`,
      { signal: this.abortController.signal },
    )
      .then((res) => {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        let apiArticles = Array.from(
          doc.querySelectorAll(
            ".predictive-search__results-list_articles .predictive-search__list-item",
          ),
        );

        if (this.blogHandle) {
          apiArticles = apiArticles.filter((li) => {
            const link = li.querySelector("a");
            return link
              ?.getAttribute("href")
              ?.includes(`/blogs/${this.blogHandle}/`);
          });
        }

        const merged = this.mergeResults(indexMatches, onPageUrls, apiArticles);
        if (Object.keys(this.cachedResults).length >= MAX_CACHE) {
          delete this.cachedResults[Object.keys(this.cachedResults)[0]];
        }
        this.cachedResults[cacheKey] = merged;
        this.renderResults(merged);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;

        if (indexMatches.length) {
          if (Object.keys(this.cachedResults).length >= MAX_CACHE) {
            delete this.cachedResults[Object.keys(this.cachedResults)[0]];
          }
          this.cachedResults[cacheKey] = indexMatches;
          this.renderResults(indexMatches);
        } else {
          this.spinner.classList.add("hidden");
        }
      });
  }

  renderResults(mergedResults) {
    this.spinner.classList.add("hidden");
    this.blogContent.classList.add("hidden");
    this.resultsContainer.classList.remove("hidden");

    if (!mergedResults.length) {
      this.resultsContainer.innerHTML =
        '<p class="blog-search__no-results">No articles found.</p>';
      return;
    }

    const list = document.createElement("ul");
    list.className = "blog__posts blog__posts--grid articles-wrapper";

    for (const item of mergedResults) {
      const li = document.createElement("li");
      li.className = "blog__post article";
      li.innerHTML = item.html;
      list.appendChild(li);
    }

    this.resultsContainer.innerHTML = "";
    this.resultsContainer.appendChild(list);
  }

  clearSearch() {
    this.input.value = "";
    this.clearBtn.classList.add("hidden");
    this.resultsContainer.classList.add("hidden");
    this.resultsContainer.innerHTML = "";
    this.spinner.classList.add("hidden");
    this.blogContent.classList.remove("hidden");
    this.abortController?.abort();
  }
}

customElements.define("blog-search", BlogSearch);
